/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useAuth } from '../userManagement/AuthContext';
import { useAutoTranslate } from '../common/T';
import './pmiTopBar.css';

export const ERP_LAST_PATH_KEY = 'erp-last-path';
export const PMI_LAST_PATH_KEY = 'pmi-last-path';
export const readPath = (key, fallback) => {
  try { return window.sessionStorage.getItem(key) || fallback; } catch { return fallback; }
};

// The two halves of the system: the ERP and the PMI platform. One click moves between them,
// each side reopening where you left it.
export function AppSwitch({ current }) {
  const at = useAutoTranslate();
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <div className="app-switch" role="tablist" aria-label={at('Switch workspace')}>
      <button type="button" role="tab" aria-selected={current === 'erp'} className={current === 'erp' ? 'is-on' : ''}
        onClick={() => current !== 'erp' && navigate(readPath(ERP_LAST_PATH_KEY, '/'))}>ERP</button>
      <button type="button" role="tab" aria-selected={current === 'pmi'} className={current === 'pmi' ? 'is-on' : ''}
        onClick={() => current !== 'pmi' && navigate(user?.is_admin === true ? readPath(PMI_LAST_PATH_KEY, '/admin/pmi-platform/overview') : '/admin/pmi-platform/tasks')}>PMI</button>
    </div>
  );
}

// Short tab names; the menu keeps the longer ones.
const SHORT = { 'MyCity Hospitality': 'Hospitality' };

const matches = (path, location) => {
  if (!path) return false;
  const [target, query] = path.split('?');
  if (query) {
    const expected = new URLSearchParams(query);
    const current = new URLSearchParams(location.search);
    if ([...expected.entries()].some(([key, value]) => current.get(key) !== value)) return false;
    return location.pathname === target;
  }
  if (target === '/admin/pmi-platform/widgets' && location.search.includes('widget=')) return false;
  return location.pathname === target || location.pathname.startsWith(`${target}/`);
};

function Section({ item, location }) {
  const at = useAutoTranslate();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (!ref.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', onKey); };
  }, [open]);
  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);

  if (!item.children?.length) {
    return (
      <Link to={item.path} className={`pmi-tab${matches(item.path, location) ? ' is-on' : ''}`}>
        {item.icon && <span className="pmi-tab__icon">{item.icon}</span>}{item.label}
      </Link>
    );
  }
  const active = item.children.some((child) => matches(child.path, location));
  const main = item.children[0];
  return (
    <div className={`pmi-tab pmi-tab--menu${active ? ' is-on' : ''}${open ? ' is-open' : ''}`} ref={ref}>
      <Link to={main.path} className="pmi-tab__main">
        {item.icon && <span className="pmi-tab__icon">{item.icon}</span>}{SHORT[item.label] || item.label}
      </Link>
      <button type="button" className="pmi-tab__more" aria-expanded={open} aria-label={at('More in {{section}}', { section: item.label })}
        onClick={() => {
          const rect = ref.current?.getBoundingClientRect();
          if (rect) setPos({ top: rect.bottom + 6, left: Math.min(rect.left, window.innerWidth - 250) });
          setOpen((value) => !value);
        }}>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="pmi-menu" role="menu" style={pos ? { top: pos.top, left: pos.left } : undefined}>
          {item.children.map((child) => (
            <Link key={child.path + child.label} to={child.path} role="menuitem" className={matches(child.path, location) ? 'is-on' : ''}>{child.label}</Link>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The PMI platform's own header: brand, ERP | PMI switch, PMI sections as tabs (with their pages in a
 * small menu) and the usual tools on the right. Replaces the ERP sidebar while working in PMI.
 */
export default function PmiTopBar({ sections, right }) {
  const at = useAutoTranslate();
  const location = useLocation();
  return (
    <header className="pmi-bar">
      <div className="pmi-bar__row">
        <Link to="/admin/pmi-platform/overview" className="pmi-brand" aria-label={at('PMI platform overview')}>
          <img src="/E-SCOOTER-LAB-Logo.svg" alt="" className="pmi-brand__logo"
            onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = '/E-SCOOTER-LAB-Logo.png'; }} />
          <span className="pmi-brand__name">PMI</span>
        </Link>
        <AppSwitch current="pmi" />
        <nav className="pmi-tabs" aria-label={at('PMI sections')}>
          {sections.map((item) => <Section key={item.id || item.path} item={item} location={location} />)}
        </nav>
        <div className="pmi-bar__right">{right}</div>
      </div>
    </header>
  );
}
